// Exemples du guide de notation des seances (page GuideNotation). Chaque
// notation est reprise des seances reelles du club ou illustre une regle du
// moteur d'analyse (seanceEngine.ts) : guideNotation.test.ts verifie que les
// exemples valides passent le moteur et que les erreurs sont bien refusees,
// pour que le guide ne decrive jamais une regle que le code n'applique pas.

export type Exemple = { notation: string; lecture: string };

export type ErreurFrequente = { notation: string; probleme: string; correction: string };

/** Exemples des sections du guide, par regle. */
export const EXEMPLES = {
  anatomie: { notation: "6x(800m@5k-R2')", lecture: '6 répétitions de 800 m à l\'allure 5 km, 2 minutes de récupération entre chaque.' },
  duree: { notation: "45'@EASY", lecture: '45 minutes en endurance facile.' },
  dureeSecondes: { notation: "6x(1'15''@5k-R1'45'')", lecture: "6 × 1'15 à l'allure 5 km, récupération 1'45." },
  distance: { notation: '10km@EASY', lecture: '10 km en endurance facile.' },
  distanceMetres: { notation: "12x(400m@3k-R2')", lecture: "12 × 400 m à l'allure 3 km, récupération 2'." },
  distanceK: { notation: '8k@MP', lecture: "8 km à l'allure marathon (« k » avant le @ = kilomètres)." },
  progressif: { notation: "30'@HP-10K", lecture: "30' en accélérant progressivement de l'allure semi à l'allure 10 km." },
  recupTemps: { notation: "8x(1'@3K-R1'30'')", lecture: "8 × 1' à l'allure 3 km, 1'30 de récupération." },
  recupSecondes: { notation: "6x(100m@3k-R0'30'')", lecture: "6 lignes droites de 100 m, 30 secondes de récupération." },
  recupDistance: { notation: "6x(1000m@10K-R400m)", lecture: '6 × 1000 m à l\'allure 10 km, 400 m de récupération trottinée.' },
  sansRecup: { notation: "4x(8'@MP)", lecture: "4 × 8' à l'allure marathon, enchaînés sans récupération." },
  repetitions: { notation: "4x(8'@HP-R2')", lecture: "4 × 8' à l'allure semi, 2' de récupération (3 récupérations : aucune après la dernière)." },
  enchainement: { notation: "2x(4'@10K-R3')+6x(1'@3K-R1')", lecture: "2 × 4' allure 10 km avec 3' de récup, y compris avant les 6 × 1' allure 3 km (R1')." },
  enchainementSansRecup: { notation: "3x(2'@10K)+6x(1'@3K-R1')", lecture: "Sans récup écrite, les 1' suivent directement les 2'." },
  enchainementRecup: { notation: "4x(5'@HP-R1')+3'@RECUP+6x(1'@5k-R1')", lecture: "4 × 5' allure semi (R1'), 3' de footing lent, puis 6 × 1' allure 5 km." },
  pyramide: { notation: "(1K@HP-R2'30'')+(2K@HP-R2'30'')+1K@HP", lecture: "1 km, 2 km, 1 km allure semi, 2'30 de récup entre chaque." },
  seanceComplete: { notation: "15'@EASY+35'@STEADY+10'@RECUP", lecture: "15' facile, 35' soutenu, 10' de retour au calme." },
  compose: { notation: "3x(8'@MP-R1'30''-3'@10k-R3')", lecture: "3 fois : 8' allure marathon, 1'30 de récup, 3' allure 10 km ; 3' de récup entre deux tours." },
  composePlus: { notation: "2x(3000m@MP-R2'30''+800m@5k-R1'15''+400m@5k)", lecture: "Dans les parenthèses, « + » vaut « - » : 2 fois 3000 m MP, 800 m 5K, 400 m 5K." },
  alternance: { notation: '7x(1000m@EASY-1000m@MP)', lecture: '7 fois 1000 m facile puis 1000 m allure marathon, sans arrêt.' },
  sequenceSansRepetition: { notation: '10k@MP-1K@RECUP-8k@MP', lecture: "Une seule fois : 10 km MP, 1 km lent, 8 km MP." },
  seriesAplaties: { notation: "2x5x(1'15''@5k-R1'15'')", lecture: "2 séries de 5, comptées comme 10 × 1'15 avec 1'15 de récup partout." },
  seriesImbriquees: { notation: "2x(5x(1'15''@5k-R1'15'')-R3')", lecture: "2 séries de 5 × 1'15, 1'15 de récup dans la série, 3' entre les séries." },
  seriesPlus: { notation: "5x(1'15''@5k-R1'45'')+2'@RECUP+5x(1'15''@5k-R1'45'')", lecture: "La même idée écrite bloc par bloc : 1'45 de récup puis 2' de footing lent entre les séries." },
  condition: { notation: "10x(1'@5k[côte]-R1'30'')", lecture: "10 côtes d'1', à l'intensité d'une allure 5 km, 1'30 de récup." },
  conditionPiste: { notation: "10x(400m@VMA[piste]-R1')", lecture: '10 × 400 m à VMA sur piste.' },
} satisfies Record<string, Exemple>;

/** Seances reelles du club (export du 02/11/2026), lues en clair. */
export const EXEMPLES_CLUB: Exemple[] = [
  { notation: "35'@EASY+6x(100m@3k-R0'30'')", lecture: "Footing de 35' suivi de 6 lignes droites de 100 m (récup 30'')." },
  { notation: "25'@EASY+20'@STEADY+15'@RECUP", lecture: "Séance combinée : 25' facile, 20' soutenu, 15' de retour au calme." },
  { notation: "10x(800m@5k-R2')", lecture: "Yasso : 10 × 800 m à l'allure 5 km, 2' de récup." },
  { notation: "4x(8'@MP-R3')", lecture: "4 × 8' allure marathon, 3' de récup." },
  { notation: "2x(15'@MP-R1'30'')+5'@10K+3'@RECUP", lecture: "2 × 15' MP (récup 1'30), 5' allure 10 km, 3' de footing lent." },
  { notation: '10k@MP+1K@RECUP+8k@MP', lecture: "10 km MP, 1 km lent, 8 km MP." },
  { notation: "3x(6'@HM-R1'-0'50''@3K-R4')", lecture: "3 fois : 6' allure semi, 1' de récup, 50'' allure 3 km ; 4' entre les tours." },
  { notation: "4x(6'@HP - R1' - 0'50''@VMA - R2'30'')", lecture: "Même principe avec un sprint à VMA ; les espaces sont tolérés." },
  { notation: "12x(600m@5k-R1'15'')+3'@RECUP+1200m@5K", lecture: "12 × 600 m allure 5 km, 3' lent, puis 1200 m allure 5 km." },
  { notation: "2x(1600m@HP-R2'-1200m@10k-R1'45''-800m@10k-R1'10''-400m@5k-R1'15'')", lecture: "Pyramide descendante répétée 2 fois, chaque distance plus rapide." },
  { notation: "15x(1'@5k[côte]-R1'30'')", lecture: "15 côtes d'1' à l'intensité 5 km." },
  { notation: "1600m@HP+1000m@EASY+3'@RECUP+1200m@10k+1000m@EASY+3'@RECUP+800m@5k+1000m@EASY+3'@RECUP+400m@3k+1000m@EASY", lecture: "Michigan : chaque fraction est suivie d'1 km facile puis de 3' lents." },
  { notation: '42km@MP', lecture: 'Marathon à allure marathon (compétition).' },
];

/** Erreurs frequentes : la notation est refusee, la correction est acceptee. */
export const ERREURS: ErreurFrequente[] = [
  {
    notation: "6x1000m@10K-R2'",
    probleme: 'Les parenthèses après « 6x » sont obligatoires.',
    correction: "6x(1000m@10K-R2')",
  },
  {
    notation: "6x(1000m@10K-R1k)",
    probleme: "Une récupération en distance s'écrit en « km » ou « m », jamais « k » (R10K serait ambigu avec l'allure 10 km).",
    correction: "6x(1000m@10K-R1km)",
  },
  {
    notation: '1h@EASY',
    probleme: "Les heures ne sont pas reconnues : écrire la durée en minutes.",
    correction: "60'@EASY",
  },
  {
    notation: "45'",
    probleme: "Chaque effort doit avoir une allure après « @ ».",
    correction: "45'@EASY",
  },
  {
    notation: "10x(1'@T-R1')",
    probleme: "Code d'allure inconnu.",
    correction: "10x(1'@VMA-R1')",
  },
  {
    notation: "[côte]10x(1'@5k-R1')",
    probleme: "La condition s'écrit collée juste après l'allure.",
    correction: "10x(1'@5k[côte]-R1')",
  },
  {
    notation: "10x(1'@5k[plage]-R1')",
    probleme: 'Condition inconnue (côte, descente, trail, piste ou tapis).',
    correction: "10x(1'@5k[trail]-R1')",
  },
];
